// utils/chartHelpers.js
// Chart Configuration and Helper Functions

// Reads from the same singleton as helpers.js via getActiveCurrency
import { getActiveCurrency } from './helpers';

/**
 * Get responsive chart dimensions based on screen size
 * @param {boolean} isMobile - Is mobile device
 * @param {boolean} isTablet - Is tablet device
 * @returns {Object} - Chart dimensions and margins
 */
export const getChartDimensions = (isMobile, isTablet) => ({
  height: isMobile ? 280 : isTablet ? 350 : 400,
  margin: {
    top: isMobile ? 10 : 20,
    right: isMobile ? 10 : isTablet ? 20 : 30,
    bottom: isMobile ? 10 : isTablet ? 20 : 30,
    left: isMobile ? 10 : isTablet ? 20 : 30
  },
  fontSize: {
    axis: isMobile ? 10 : isTablet ? 12 : 14,
    legend: isMobile ? 10 : isTablet ? 12 : 14,
    label: isMobile ? 12 : isTablet ? 14 : 16
  }
});

/**
 * Format currency for display — uses user's selected currency via singleton
 * @param {number} value - Amount to format
 * @param {boolean} compact - Use compact notation (K, M)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value, compact = false) => {
  const safeValue = Number(value) || 0;
  const currency = getActiveCurrency();
  // Derive a simple symbol for compact notation
  const symbolMap = { EUR: '€', USD: '$', GBP: '£', JPY: '¥', CHF: 'Fr', CAD: 'CA$', AUD: 'A$' };
  const sym = symbolMap[currency] || currency;

  if (compact && Math.abs(safeValue) >= 1000) {
    if (Math.abs(safeValue) >= 1000000) {
      return `${sym}${(safeValue / 1000000).toFixed(1)}M`;
    }
    return `${sym}${(safeValue / 1000).toFixed(1)}K`;
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(safeValue);
  } catch {
    return `${sym}${safeValue.toFixed(0)}`;
  }
};

/**
 * Get color by value (positive/negative)
 * @param {number} value - Value to evaluate
 * @returns {string} - Color code
 */
export const getValueColor = (value) => {
  if (value > 0) return '#10b981'; // Green
  if (value < 0) return '#ef4444'; // Red
  return '#6b7280'; // Gray
};

/**
 * Get budget utilization color
 * @param {number} percentage - Budget utilization percentage
 * @returns {string} - Color code
 */
export const getBudgetColor = (percentage) => {
  if (percentage <= 70) return '#10b981'; // Green
  if (percentage <= 90) return '#f59e0b'; // Yellow/Amber
  if (percentage <= 100) return '#f97316'; // Orange
  return '#ef4444'; // Red
};

/**
 * Create custom tooltip style
 * @param {string} themeMode - 'light' or 'dark'
 * @returns {Object} - Tooltip style object
 */
export const getTooltipStyle = (themeMode) => ({
  backgroundColor: themeMode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
  border: `1px solid ${themeMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
  borderRadius: '8px',
  padding: '12px',
  color: themeMode === 'light' ? '#1f2937' : '#ffffff',
  fontSize: '14px',
  fontWeight: '500',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(10px)'
});

/**
 * Create gradient definitions for charts
 * @param {Array} colors - Array of color stops
 * @returns {Array} - Gradient definition objects
 */
export const createGradients = (colors) => {
  return colors.map((color, index) => ({
    id: `gradient-${index}`,
    color,
    stops: [
      { offset: '0%', color, opacity: 0.9 },
      { offset: '100%', color, opacity: 0.6 }
    ]
  }));
};

/**
 * Get axis style configuration
 * @param {string} themeMode - 'light' or 'dark'
 * @param {boolean} isMobile - Is mobile device
 * @returns {Object} - Axis configuration
 */
export const getAxisStyle = (themeMode, isMobile) => ({
  tick: {
    fontSize: isMobile ? 10 : 14,
    fontWeight: 500,
    fill: themeMode === 'light' ? '#1f2937' : '#ffffff'
  },
  axisLine: {
    stroke: themeMode === 'light' ? '#e5e7eb' : '#374151',
    strokeWidth: 1
  },
  tickLine: {
    stroke: themeMode === 'light' ? '#e5e7eb' : '#374151',
    strokeWidth: 1
  }
});

/**
 * Enhanced color palette for charts
 */
export const CHART_COLORS = {
  primary: {
    income: '#10b981',
    expense: '#ef4444',
    savings: '#3b82f6',
    budget: '#8b5cf6'
  },
  categories: [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe',
    '#43e97b', '#38f9d7', '#fa709a', '#fee140', '#a8edea', '#fed6e3',
    '#d299c2', '#fef9d7', '#ff9a9e', '#fecfef', '#ffecd2', '#fcb69f',
    '#a8e6cf', '#dcedc1', '#ffd3a5', '#fd9853', '#74b9ff', '#0984e3'
  ],
  status: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    neutral: '#6b7280'
  },
  gradients: {
    income: {
      start: '#22c55e',
      end: '#10b981'
    },
    expense: {
      start: '#ef4444',
      end: '#dc2626'
    },
    savings: {
      start: '#3b82f6',
      end: '#2563eb'
    }
  }
};

/**
 * Calculate sparkline data (mini trend for cards)
 * @param {Array} data - Historical data points
 * @param {number} points - Number of points to include
 * @returns {Array} - Simplified data for sparkline
 */
export const generateSparklineData = (data, points = 7) => {
  if (!data || data.length === 0) return [];
  
  const step = Math.max(1, Math.floor(data.length / points));
  const sparkline = [];
  
  for (let i = 0; i < data.length; i += step) {
    if (sparkline.length >= points) break;
    sparkline.push(data[i]);
  }
  
  return sparkline;
};

/**
 * Custom label formatter for pie charts
 * @param {Object} entry - Chart entry
 * @param {boolean} showPercentage - Show percentage instead of value
 * @returns {string} - Formatted label
 */
export const formatPieLabel = (entry, showPercentage = true) => {
  if (showPercentage) {
    return `${entry.percentage?.toFixed(1)}%`;
  }
  return formatCurrency(entry.value);
};

/**
 * Calculate responsive font sizes
 * @param {boolean} isMobile - Is mobile device
 * @param {boolean} isTablet - Is tablet device
 * @returns {Object} - Font size configuration
 */
export const getResponsiveFontSizes = (isMobile, isTablet) => ({
  title: isMobile ? 16 : isTablet ? 18 : 20,
  subtitle: isMobile ? 12 : isTablet ? 14 : 16,
  value: isMobile ? 24 : isTablet ? 32 : 40,
  label: isMobile ? 10 : isTablet ? 12 : 14,
  axis: isMobile ? 10 : isTablet ? 12 : 14,
  legend: isMobile ? 10 : isTablet ? 12 : 14
});

/**
 * Create animated gradient for enhanced visuals
 * @param {string} id - Gradient ID
 * @param {string} startColor - Start color
 * @param {string} endColor - End color
 * @param {string} direction - 'vertical' or 'horizontal'
 * @returns {Object} - Gradient configuration
 */
export const createAnimatedGradient = (id, startColor, endColor, direction = 'vertical') => ({
  id,
  x1: direction === 'horizontal' ? '0' : '0',
  y1: direction === 'horizontal' ? '0' : '0',
  x2: direction === 'horizontal' ? '1' : '0',
  y2: direction === 'horizontal' ? '0' : '1',
  stops: [
    { offset: '5%', color: startColor, opacity: 0.8 },
    { offset: '95%', color: endColor, opacity: 0.1 }
  ]
});

/**
 * Format percentage with sign
 * @param {number} value - Percentage value
 * @returns {string} - Formatted percentage with + or - sign
 */
export const formatPercentage = (value) => {
  const formatted = Math.abs(value).toFixed(1);
  if (value > 0) return `+${formatted}%`;
  if (value < 0) return `-${formatted}%`;
  return `${formatted}%`;
};

/**
 * Get trend arrow icon name based on value
 * @param {number} value - Value to evaluate
 * @returns {string} - 'up', 'down', or 'neutral'
 */
export const getTrendDirection = (value) => {
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'neutral';
};

/**
 * Create treemap data structure from categories
 * @param {Array} categories - Category data
 * @returns {Object} - Treemap compatible data structure
 */
export const createTreemapData = (categories) => ({
  name: 'Expenses',
  children: categories.map(cat => ({
    name: cat.name,
    size: cat.value,
    color: cat.color,
    budgetUtilization: cat.budgetUtilization,
    spent: cat.value,
    budget: cat.budget
  }))
});

/**
 * Custom treemap content renderer
 * @param {Object} props - Recharts props
 * @returns {React.Element} - Custom content
 */
export const renderTreemapContent = (props) => {
  const { x, y, width, height, name, size, color } = props;
  
  if (width < 50 || height < 30) return null; // Too small to display
  
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: '#fff',
          strokeWidth: 2,
          opacity: 0.8
        }}
      />
      {width > 80 && height > 40 && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 10}
            textAnchor="middle"
            fill="#fff"
            fontSize={12}
            fontWeight="600"
          >
            {name}
          </text>
          <text
            x={x + width / 2}
            y={y + height / 2 + 10}
            textAnchor="middle"
            fill="#fff"
            fontSize={10}
          >
            {formatCurrency(size)}
          </text>
        </>
      )}
    </g>
  );
};

/**
 * Calculate optimal chart height based on data density
 * @param {number} dataPoints - Number of data points
 * @param {boolean} isMobile - Is mobile device
 * @returns {number} - Optimal height in pixels
 */
export const getOptimalChartHeight = (dataPoints, isMobile) => {
  if (isMobile) {
    if (dataPoints > 10) return 320;
    return 280;
  }
  
  if (dataPoints > 20) return 500;
  if (dataPoints > 10) return 450;
  return 400;
};

/**
 * Debounce function for performance
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
